import { create } from 'zustand'
import {TextDataType, enUSTextData} from "@/datas/LocaleData";
import {UserProfile, PuzzleChat, Puzzle, PuzzleTab} from "@/utils/util_types";
import {getSystemLanguageCode} from "@/utils/util_func";
import { getStorage, setStorage } from "@/utils/util_storage";
import { readLanguageCookie, writeLanguageCookie } from '@/i18n/client';
import { resolveLanguageCode } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';

export { getLanguageDataByCode } from '@/i18n/language-data';

// ═══════════════════════════════════════════════
// Theme Store (dark/light mode)
// ═══════════════════════════════════════════════
type ThemeMode = 'light' | 'dark';
type ThemeState = {
    theme: ThemeMode
}
type ThemeAction = {
    setTheme: (theme: ThemeMode) => void
    initTheme: () => Promise<void>
}

let themeTransitionTimer: ReturnType<typeof setTimeout> | undefined;

function applyThemeClass(theme: ThemeMode, animate: boolean) {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    if (animate) {
        root.classList.add('theme-transition');
        if (themeTransitionTimer) clearTimeout(themeTransitionTimer);
        themeTransitionTimer = setTimeout(() => {
            root.classList.remove('theme-transition');
            themeTransitionTimer = undefined;
        }, 360);
    }
    root.classList.toggle('dark', theme === 'dark');
}

export const useThemeStore = create<ThemeState & ThemeAction>((set) => ({
    theme: 'dark',
    setTheme: (theme: ThemeMode) => {
        set({ theme });
        applyThemeClass(theme, true);
        setStorage('theme', theme);
    },
    initTheme: async () => {
        const savedTheme = await getStorage('theme');
        // Web version defaults to dark for the noir aesthetic
        const theme: ThemeMode = savedTheme === 'light' ? 'light' : 'dark';
        set({ theme });
        applyThemeClass(theme, false);
    },
}))

type LangDataState = {
    langData: TextDataType
    languageCode: string
    langReady: boolean
}
type LangDataAction = {
    updateLangData: (langData: TextDataType) => void
    updateLanguageCode: (code: string) => Promise<void>
    initLanguageFromSystem: (requestLanguageCode?: string | null) => Promise<void>
}
export const useLangDataStore = create<LangDataState&LangDataAction>((set) => ({
    langData: enUSTextData,
    languageCode: 'en-US',
    langReady: false,
    updateLangData: (langData: TextDataType) => set({langData: langData}),
    updateLanguageCode: async (code: string) => {
        const normalizedCode = resolveLanguageCode(code) ?? 'en-US';
        const langData = getLanguageDataByCode(normalizedCode);
        set({languageCode: normalizedCode, langData: langData, langReady: true});
        try {
            writeLanguageCookie(normalizedCode);
            await setStorage('language_code', normalizedCode);
        } catch (error) {
            console.error('保存语言设置失败:', error);
        }
    },
    initLanguageFromSystem: async (requestLanguageCode) => {
        try {
            const requestedCode = resolveLanguageCode(requestLanguageCode);
            const cookieCode = resolveLanguageCode(readLanguageCookie());
            const savedLangCode = await getStorage('language_code');
            const storedCode = resolveLanguageCode(savedLangCode);
            const systemLangCode = resolveLanguageCode(await getSystemLanguageCode());
            // A public locale in the URL is canonical. Preferences remain the fallback
            // for app routes that do not carry a locale prefix.
            const languageCode = requestedCode ?? storedCode ?? cookieCode ?? systemLangCode ?? 'en-US';
            const langData = getLanguageDataByCode(languageCode);
            set({languageCode, langData, langReady: true});
            try {
                writeLanguageCookie(languageCode);
                await setStorage('language_code', languageCode);
            } catch (error) {
                console.error('保存语言设置失败:', error);
            }
        } catch (error) {
            console.error('初始化系统语言失败:', error);
            set({langReady: true}); // 失败也标记 ready，用默认语言
        }
    }
}))

type UserState = {
    userProfile: UserProfile
    isLogin: boolean
    authReady: boolean
}
type UserAction = {
    updateUser: (user: UserProfile) => void
    updateIsLogin: (isLogin: boolean) => void
    updateAuthReady: (authReady: boolean) => void
    resetUser: () => void
    addLikedPuzzle: (puzzleId: string) => void
    removeLikedPuzzle: (puzzleId: string) => void
    setLikePuzzles: (likePuzzles: string[]) => void
}
export const EMPTY_USER_PROFILE: UserProfile = {
    id: "",
    username: "",
    avatar: null,
    coins: 0,
    solvePuzzleNum: 0,
    puzzleChats: [],
    likePuzzles: [],
};

export const useUserStore = create<UserState&UserAction>((set) => ({
    userProfile: { ...EMPTY_USER_PROFILE },
    isLogin: false,
    authReady: false,
    updateUser: (user: UserProfile) => set({userProfile: user}),
    updateIsLogin: (isLogin: boolean) => set({isLogin: isLogin}),
    updateAuthReady: (authReady: boolean) => set({authReady}),
    resetUser: () => set({userProfile: { ...EMPTY_USER_PROFILE }, isLogin: false}),
    addLikedPuzzle: (puzzleId: string) => set((state) => {
        const currentLikePuzzles = state.userProfile.likePuzzles || [];
        if (currentLikePuzzles.includes(puzzleId)) return state;
        return { userProfile: { ...state.userProfile, likePuzzles: [...currentLikePuzzles, puzzleId] } };
    }),
    removeLikedPuzzle: (puzzleId: string) => set((state) => {
        const currentLikePuzzles = state.userProfile.likePuzzles || [];
        return { userProfile: { ...state.userProfile, likePuzzles: currentLikePuzzles.filter(id => id !== puzzleId) } };
    }),
    setLikePuzzles: (likePuzzles: string[]) => set((state) => ({
        userProfile: { ...state.userProfile, likePuzzles: likePuzzles }
    }))
}))

// 谜题列表缓存 Store
type PuzzleListState = {
    activePuzzleChats: PuzzleChat[]
    completedPuzzleChats: PuzzleChat[]
}
type PuzzleListAction = {
    setActivePuzzleChats: (chats: PuzzleChat[]) => void
    setCompletedPuzzleChats: (chats: PuzzleChat[]) => void
    setPuzzleChats: (active: PuzzleChat[], completed: PuzzleChat[]) => void
    clearPuzzleChats: () => void
}
export const usePuzzleListStore = create<PuzzleListState&PuzzleListAction>((set) => ({
    activePuzzleChats: [],
    completedPuzzleChats: [],
    setActivePuzzleChats: (chats: PuzzleChat[]) => set({activePuzzleChats: chats}),
    setCompletedPuzzleChats: (chats: PuzzleChat[]) => set({completedPuzzleChats: chats}),
    setPuzzleChats: (active: PuzzleChat[], completed: PuzzleChat[]) => set({ activePuzzleChats: active, completedPuzzleChats: completed }),
    clearPuzzleChats: () => set({activePuzzleChats: [], completedPuzzleChats: []})
}))

// 发现页面缓存 Store
type DiscoverState = {
    latestPuzzles: Puzzle[]
    hotTodayPuzzles: Puzzle[]
    hotWeekPuzzles: Puzzle[]
    rawPuzzles: any[]
}
type DiscoverAction = {
    setLatestPuzzles: (puzzles: Puzzle[]) => void
    setHotTodayPuzzles: (puzzles: Puzzle[]) => void
    setHotWeekPuzzles: (puzzles: Puzzle[]) => void
    setPuzzles: (tab: PuzzleTab, puzzles: Puzzle[]) => void
    setRawPuzzles: (raw: any[]) => void
    clearPuzzles: () => void
}
export const useDiscoverStore = create<DiscoverState&DiscoverAction>((set) => ({
    latestPuzzles: [],
    hotTodayPuzzles: [],
    hotWeekPuzzles: [],
    rawPuzzles: [],
    setLatestPuzzles: (puzzles: Puzzle[]) => set({latestPuzzles: puzzles}),
    setHotTodayPuzzles: (puzzles: Puzzle[]) => set({hotTodayPuzzles: puzzles}),
    setHotWeekPuzzles: (puzzles: Puzzle[]) => set({hotWeekPuzzles: puzzles}),
    setPuzzles: (tab: PuzzleTab, puzzles: Puzzle[]) => {
        if (tab === 'latest') set({latestPuzzles: puzzles})
        else if (tab === 'hot-today') set({hotTodayPuzzles: puzzles})
        else if (tab === 'hot-week') set({hotWeekPuzzles: puzzles})
    },
    setRawPuzzles: (raw: any[]) => set({rawPuzzles: raw}),
    clearPuzzles: () => set({ latestPuzzles: [], hotTodayPuzzles: [], hotWeekPuzzles: [], rawPuzzles: [] })
}))

// 个人中心谜题缓存 Store
type ProfilePuzzleState = {
    createdPuzzles: Puzzle[]
    likedPuzzles: Puzzle[]
}
type ProfilePuzzleAction = {
    setCreatedPuzzles: (puzzles: Puzzle[]) => void
    setLikedPuzzles: (puzzles: Puzzle[]) => void
    clearPuzzles: () => void
}
export const useProfilePuzzleStore = create<ProfilePuzzleState&ProfilePuzzleAction>((set) => ({
    createdPuzzles: [],
    likedPuzzles: [],
    setCreatedPuzzles: (puzzles: Puzzle[]) => set({createdPuzzles: puzzles}),
    setLikedPuzzles: (puzzles: Puzzle[]) => set({likedPuzzles: puzzles}),
    clearPuzzles: () => set({createdPuzzles: [], likedPuzzles: []})
}))

type NotificationState = {
    unreadCount: number
}
type NotificationAction = {
    setUnreadCount: (count: number) => void
    clearUnreadCount: () => void
}
export const useNotificationStore = create<NotificationState & NotificationAction>((set) => ({
    unreadCount: 0,
    setUnreadCount: (count) => set({ unreadCount: Math.max(0, count) }),
    clearUnreadCount: () => set({ unreadCount: 0 }),
}))
